import { Store } from "@ngrx/store";
import { batchActions } from "@obg/ngrx-utils";
import { Message, MessageAction, MessageFilter, MessageSelector, MessageType } from "@obg/ngx.core";
import { CoreContentState, MessageCodeFallbackService, TranslationService } from "@obg/ngx.core.content";
import { LoggingService, LogType } from "@obg/ngx.global.evo";
import { last } from "lodash-es";
import { from, Observable, of } from "rxjs";
import { filter, map, switchMap } from "rxjs/operators";

import { MessageDisplayOptions } from "./message-display.model";

export abstract class MessageDisplayService<T> {
	constructor(
		protected messageAction: MessageAction,
		protected messageCodeFallbackService: MessageCodeFallbackService,
		protected messageSelector: MessageSelector,
		protected store: Store<CoreContentState>,
		protected translationService: TranslationService,
		protected loggingService: LoggingService
	) {}

	/**
	 * Displays the message/s passed from parameters
	 *
	 * @param message used to define the messages to be shown
	 * @param options that can be used to affect the presentation of data
	 * @returns data message container
	 */
	abstract present(message: Message | Message[], options: any): T;

	/**
	 * Gets message/s from application state to present them using `present` method
	 *
	 * @param options.beforeDisplay function to call before presenting the message/s
	 * @param options.keepMessage option to keep the messages in the state after present the message/s
	 * @returns the result of the implemented present method
	 */
	protected presentOnDispatch(messageFilter: MessageFilter, options: any = {}): Observable<T> {
		return this.store.pipe(
			this.messageSelector.getAll$(messageFilter),
			filter((messages: Message[]) => !!messages.length),
			switchMap((messages: Message[]) => {
				if (options.beforeDisplay) {
					return from(options.beforeDisplay()).pipe(map(() => messages));
				}
				return of(messages);
			}),
			map((messages: Message[]) => {
				const messageContainer = this.present(messages, options);
				if (!options.keepMessages) {
					if (messageFilter?.context) {
						this.store.dispatch(
							Array.isArray(messageFilter.context)
								? batchActions(
										...messageFilter.context.map((context) =>
											this.messageAction.clearByContext(context)
										)
									)
								: this.messageAction.clearByContext(messageFilter.context)
						);
					} else {
						for (const i in messages) {
							if (messages.hasOwnProperty(i)) {
								this.store.dispatch(this.messageAction.clearByCode(messages[i].messageCode));
							}
						}
					}
				}
				return messageContainer;
			})
		);
	}

	/**
	 * Returns the (translated) message text when given in options.
	 * Otherwise, returns the (translated) message based on the last message code.
	 * Otherwise (if the code was null), the fallback code from the options is used.
	 */
	protected getMessageText(message: Message | Message[], options: MessageDisplayOptions): string {
		if (options.message) {
			if (options.doNotTranslate) {
				return options.message;
			}

			return this.getTranslationInstant(this.getMessageKey(options.translationPrefix, options.message));
		}

		const lastMessage = this.getLastMessage(message);
		let messageCode = "";

		if (lastMessage?.messageCode) {
			messageCode = lastMessage.messageCode;
		} else if (options.fallbackMessageCode) {
			messageCode = options.fallbackMessageCode;
		}

		if (messageCode) {
			return this.messageCodeFallbackService.get(this.getMessageKey(options.translationPrefix, messageCode));
		}

		return messageCode;
	}

	protected getLastMessage(message: Message | Message[]): Message | undefined {
		return Array.isArray(message) ? last(message) : message;
	}

	protected getMessageType(message: Message | Message[]): MessageType | undefined {
		const lastMessage = this.getLastMessage(message);
		return lastMessage?.type;
	}

	protected getMessageKey(translationPrefix: string | undefined, message: string): string {
		return (translationPrefix ? `${translationPrefix}.${message}` : message).toLowerCase();
	}

	protected getTranslationInstant(key: string): string {
		const translated = this.translationService.getByKey(key);

		if (translated === undefined || translated === key) {
			this.loggingService.log(LogType.Warn, `TranslationService returned ${translated} for key ${key}`);
		}

		return translated;
	}
}
